import { PerformanceData, PerformanceDataRead } from '../types/performance';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Normalize the unified API response to frontend-friendly format
 */
const normalisePerformanceData = (
  raw: PerformanceDataRead,
  horseId: string
): PerformanceData => ({
  // Horse profile
  horseKey: raw.unified_horse_key ?? horseId,
  horseName: raw.unified_horse_name ?? null,
  sireName: raw.unified_sire_name ?? null,
  damName: raw.unified_dam_name ?? null,
  damSire: raw.unified_dam_sire ?? null,
  birthYear: raw.unified_birth_year ?? null,
  sex: raw.unified_sex ?? null,
  color: raw.unified_color ?? null,
  heightCm: raw.unified_height_cm ?? null,
  feiId: raw.unified_fei_id ?? null,
  studbook: raw.unified_studbook ?? null,
  cmhUrl: raw.unified_cmh_url ?? null,

  // Owner info
  owner: raw.unified_owner ?? null,
  breeder: raw.unified_breeder ?? null,

  // Rider history
  riderHistory: raw.unified_rider_history ?? [],
  numUniqueRiders: raw.unified_num_unique_riders ?? 0,
  numRidingPeriods: raw.unified_num_riding_periods ?? 0,

  // Aggregated stats
  firstEventDate: raw.unified_first_event_date ?? null,
  lastEventDate: raw.unified_last_event_date ?? null,
  totalEvents: raw.unified_total_events ?? 0,
  horseLocation: raw.unified_horse_location ?? null,
  horseLevel: raw.unified_horse_level ?? null,
  totalPrizes: raw.unified_total_prizes ?? null,

  // Source IDs
  horsetelexId: raw.horsetelex_horses_id ?? null,
  clipmyhorseId: raw.clipmyhorse_horses_cmh_horse_id ?? null,

  // Monthly arrays
  months: raw.unified_months ?? [],
  maxObstacleHeight: raw.unified_monthly_max_height_cm ?? [],
  sumPrizeInEuro: raw.unified_monthly_sum_prize_eur ?? [],
  monthlyClearRoundRate: raw.unified_monthly_clear_round_rate ?? [],
  monthlyNumRounds: raw.unified_monthly_num_rounds ?? [],

  // Moving averages
  meanClearRoundMovingAvg: raw.unified_mean_clear_round_moving_avg ?? [],
  meanFirstRoundFaultsMovingAvg: raw.unified_mean_first_round_faults_moving_avg ?? [],
  meanRelativeRankMovingAvg: raw.unified_mean_relative_rank_moving_avg ?? [],
  meanTop10MovingAvg: raw.unified_mean_top_10_moving_avg ?? [],
  meanClassClearRoundRateMovingAvg: raw.unified_mean_class_clear_round_rate_moving_avg ?? [],
  meanClassTop10RateMovingAvg: raw.unified_mean_class_top_10_rate_moving_avg ?? [],
  meanClassAvgFaultsMovingAvg: raw.unified_mean_class_avg_faults_moving_avg ?? [],

  // Entries - pass through as-is since they use unified_ prefix
  classEntries: raw.unified_class_entries ?? [],

  // Metadata
  generatedAt: raw.generated_at ?? null,
  movingAvgWindow: raw.moving_avg_window ?? null,
});

async function fetchFromApi(horseId: string): Promise<PerformanceDataRead | null> {
  const url = `${API_BASE_URL}/horses/${horseId.trim()}/performance`;

  try {
    const token = localStorage.getItem('access_token');
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Add ngrok header if using ngrok URL
    if (API_BASE_URL.includes('ngrok')) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    const response = await fetch(url, { headers });
    if (response.status === 404) {
      // No performance data for this horse - this is expected
      return null;
    }
    if (!response.ok) {
      console.warn('Performance API returned', response.status);
      return null;
    }
    return (await response.json()) as PerformanceDataRead;
  } catch (error) {
    console.warn('Performance API fetch failed', error);
    return null;
  }
}

export async function loadPerformanceData(
  horseId: string | null | undefined
): Promise<PerformanceData | null> {
  if (!horseId) return null;

  const apiResult = await fetchFromApi(horseId);
  if (apiResult) {
    return normalisePerformanceData(apiResult, horseId);
  }

  return null;
}

export async function loadBatchSummary(
  horseIds: string[]
): Promise<Map<string, number | null>> {
  const result = new Map<string, number | null>();
  if (!horseIds.length) return result;

  try {
    const token = localStorage.getItem('access_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (API_BASE_URL.includes('ngrok')) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    const response = await fetch(`${API_BASE_URL}/horses/performance/batch-summary`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ horse_ids: horseIds }),
    });

    if (!response.ok) {
      console.warn('Batch summary API returned', response.status);
      return result;
    }

    const data = await response.json();
    const summaries = data.summaries || {};
    for (const [id, summary] of Object.entries<{ max_height_cm: number | null }>(summaries)) {
      result.set(id, summary.max_height_cm ?? null);
    }
  } catch (error) {
    console.warn('Batch summary fetch failed', error);
  }

  return result;
}

export const isPerformanceDataEnabled = (): boolean => true;
